import type { Bbox3 } from "../geo/bbox";
import { emptyBbox3, growBbox3, isEmptyBbox } from "../geo/bbox";
import type { MeshBundle, MeshTileEntry } from "./meshBundle";

/**
 * Many small probes beat a few large ones: a tile's extreme vertices sit at arbitrary
 * depths in the vertex block (in the reference data, one tile's minimum easting is 58 %
 * of the way through), so what matters is how many places are looked at, not how many
 * bytes are read from each.
 */
const SAMPLE_BYTES = 32 * 1024;
const PROBE_BYTES = 4 * 1024;
const MIN_SAMPLES = 6;
const MAX_SAMPLES = 10;
/** One sample per this much vertex text, between the bounds above. */
const BYTES_PER_SAMPLE = 512 * 1024;
/** Sampled extents are approximate, so they are padded before being used for culling. */
const SAFETY_MARGIN = 0.4;

/**
 * Estimates a tile's extent in the OBJ's local coordinates by reading a few small slices.
 *
 * The alternative — downloading every tile to learn where it is — would defeat the point
 * of streaming: the reference archive holds 1.07 GB of OBJ text, while this reads a few
 * hundred KB per tile. Accuracy matters because the estimate decides load order and
 * culling, so the vertex block is located first and samples are spread across all of it;
 * sampling only the head of a 50 MB tile covers a fraction of its footprint. The exact
 * extent replaces the estimate as soon as the tile is parsed.
 */
export async function estimateObjBounds(
  bundle: MeshBundle,
  tile: MeshTileEntry,
  signal?: AbortSignal,
): Promise<Bbox3 | null> {
  const size = tile.objSize || bundle.sizeOf(tile.objPath);
  if (!size) return null;

  const read = (offset: number, length: number) =>
    bundle.readSlice(tile.objPath, offset, Math.min(length, size - offset), signal);
  if (!read(0, 1)) return null;

  const vertexEnd = await findVertexBlockEnd(read, size);
  const sampleCount = Math.max(
    MIN_SAMPLES,
    Math.min(MAX_SAMPLES, Math.round(vertexEnd / BYTES_PER_SAMPLE) || MIN_SAMPLES),
  );

  const bbox = emptyBbox3();
  let sampled = 0;

  // Spread the samples over the vertex block, including its very start and end. The
  // offsets are independent of one another, so all of them go out at once rather than
  // waiting on each round trip in turn — with a network gate already limiting real
  // concurrency (see `HttpRangeSource`), sequential awaits here just add up latency.
  const span = Math.max(0, vertexEnd - SAMPLE_BYTES);
  const offsets = Array.from({ length: sampleCount }, (_, i) =>
    sampleCount === 1 ? 0 : Math.floor((span * i) / (sampleCount - 1)),
  );
  const requests = offsets.map((offset) => read(offset, SAMPLE_BYTES));
  if (requests.some((request) => !request)) return null;
  const chunks = await Promise.all(requests as Array<Promise<Uint8Array>>);
  for (let i = 0; i < chunks.length; i++) {
    if (chunks[i].length === 0) continue;
    sampled += parseVertexLines(chunks[i], offsets[i] > 0, bbox);
  }

  if (sampled === 0 || isEmptyBbox(bbox)) return null;

  const padX = Math.max((bbox.maxX - bbox.minX) * SAFETY_MARGIN, 0.5);
  const padY = Math.max((bbox.maxY - bbox.minY) * SAFETY_MARGIN, 0.5);
  const padZ = Math.max((bbox.maxZ - bbox.minZ) * SAFETY_MARGIN, 0.5);
  return {
    minX: bbox.minX - padX,
    maxX: bbox.maxX + padX,
    minY: bbox.minY - padY,
    maxY: bbox.maxY + padY,
    minZ: bbox.minZ - padZ,
    maxZ: bbox.maxZ + padZ,
  };
}

/**
 * Finds roughly where the `v` lines stop, by binary search over small probes.
 *
 * OBJ writes its sections in order (`v`, then `vt`, then `f`), so "does the line at this
 * offset start with `v `" is monotonic and a dozen 4 KB probes locate the boundary in a
 * 50 MB file. A file that interleaves sections still yields a usable answer because the
 * result only bounds where samples are taken.
 */
async function findVertexBlockEnd(
  read: (offset: number, length: number) => Promise<Uint8Array> | null,
  size: number,
): Promise<number> {
  let low = 0;
  let high = size;
  for (let step = 0; step < 24 && high - low > PROBE_BYTES; step++) {
    const middle = Math.floor((low + high) / 2);
    const request = read(middle, PROBE_BYTES);
    if (!request) break;
    const chunk = await request;
    if (chunk.length === 0) break;
    if (startsVertexLine(chunk)) low = middle;
    else high = middle;
  }
  // Never return zero: a tiny file is entirely vertex text as far as sampling cares.
  return Math.max(low, Math.min(size, PROBE_BYTES));
}

/** True when the first complete line in `chunk` is a `v ` vertex line. */
function startsVertexLine(chunk: Uint8Array): boolean {
  let i = 0;
  while (i < chunk.length && chunk[i] !== 10) i++;
  i++;
  return i + 1 < chunk.length && chunk[i] === 118 /* v */ && isSpaceCode(chunk[i + 1]);
}

/**
 * Reads a tile's exact extent by scanning its whole vertex block.
 *
 * An OBJ's `v` lines are only about 12 % of the file (130 MB across the 1.07 GB reference
 * mesh, one sequential read per tile), so exact extents cost a fraction of the archive and
 * far fewer requests than dense sampling. This runs in the background to replace the
 * estimates once the viewer is already usable.
 */
export async function exactObjBounds(
  bundle: MeshBundle,
  tile: MeshTileEntry,
  signal?: AbortSignal,
): Promise<Bbox3 | null> {
  const size = tile.objSize || bundle.sizeOf(tile.objPath);
  if (!size) return null;
  const read = (offset: number, length: number) =>
    bundle.readSlice(tile.objPath, offset, Math.min(length, size - offset), signal);
  if (!read(0, 1)) return null;

  // Round the block end up a little: the binary search resolves to within a probe.
  const vertexEnd = Math.min(size, (await findVertexBlockEnd(read, size)) + PROBE_BYTES);
  const request = read(0, vertexEnd);
  if (!request) return null;
  const bytes = await request;

  const bbox = emptyBbox3();
  const found = scanVertexBytes(bytes, bbox);
  return found > 0 && !isEmptyBbox(bbox) ? bbox : null;
}

/**
 * Byte-level scan for `v x y z` lines. Kept off the string path because a vertex block can
 * be tens of megabytes and decoding it would double the memory for no benefit.
 */
function scanVertexBytes(bytes: Uint8Array, bbox: Bbox3): number {
  let i = 0;
  let count = 0;
  const length = bytes.length;
  while (i < length) {
    const lineStart = i;
    while (i < length && bytes[i] !== 10) i++;
    const lineEnd = i;
    i++;
    if (lineEnd - lineStart < 6) continue;
    if (bytes[lineStart] !== 118 /* v */ || !isSpaceCode(bytes[lineStart + 1])) continue;
    // A truncated final line would parse as a bogus coordinate; require a terminator.
    if (lineEnd >= length) break;

    let cursor = lineStart + 2;
    const x = readDouble(bytes, cursor, lineEnd);
    const y = readDouble(bytes, x.next, lineEnd);
    const z = readDouble(bytes, y.next, lineEnd);
    cursor = z.next;
    if (Number.isFinite(x.value) && Number.isFinite(y.value) && Number.isFinite(z.value)) {
      growBbox3(bbox, x.value, y.value, z.value);
      count++;
    }
  }
  return count;
}

function readDouble(bytes: Uint8Array, i: number, end: number): { value: number; next: number } {
  while (i < end && isSpaceCode(bytes[i])) i++;
  let sign = 1;
  if (i < end && (bytes[i] === 45 || bytes[i] === 43)) {
    if (bytes[i] === 45) sign = -1;
    i++;
  }
  let value = 0;
  let digits = 0;
  while (i < end && bytes[i] >= 48 && bytes[i] <= 57) {
    value = value * 10 + (bytes[i] - 48);
    digits++;
    i++;
  }
  if (i < end && bytes[i] === 46) {
    i++;
    let scale = 0.1;
    while (i < end && bytes[i] >= 48 && bytes[i] <= 57) {
      value += (bytes[i] - 48) * scale;
      scale *= 0.1;
      digits++;
      i++;
    }
  }
  if (digits === 0) return { value: NaN, next: i };
  if (i < end && (bytes[i] === 101 || bytes[i] === 69)) {
    i++;
    let expSign = 1;
    if (i < end && (bytes[i] === 45 || bytes[i] === 43)) {
      if (bytes[i] === 45) expSign = -1;
      i++;
    }
    let exponent = 0;
    while (i < end && bytes[i] >= 48 && bytes[i] <= 57) {
      exponent = exponent * 10 + (bytes[i] - 48);
      i++;
    }
    value *= 10 ** (expSign * exponent);
  }
  return { value: sign * value, next: i };
}

/** Accumulates `v x y z` lines found in a raw chunk. Returns how many were read. */
function parseVertexLines(chunk: Uint8Array, skipFirstPartialLine: boolean, bbox: Bbox3): number {
  const text = new TextDecoder("latin1").decode(chunk);
  let start = 0;
  if (skipFirstPartialLine) {
    const firstBreak = text.indexOf("\n");
    if (firstBreak < 0) return 0;
    start = firstBreak + 1;
  }
  // The final line is very likely truncated mid-number, so it is dropped.
  const lastBreak = text.lastIndexOf("\n");
  if (lastBreak <= start) return 0;

  let count = 0;
  let cursor = start;
  while (cursor < lastBreak) {
    const lineEnd = text.indexOf("\n", cursor);
    const end = lineEnd < 0 ? lastBreak : lineEnd;
    if (text.charCodeAt(cursor) === 118 /* v */ && isSpaceCode(text.charCodeAt(cursor + 1))) {
      const parts = text.slice(cursor + 2, end).trim().split(/\s+/);
      if (parts.length >= 3) {
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        const z = Number(parts[2]);
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
          growBbox3(bbox, x, y, z);
          count++;
        }
      }
    }
    cursor = end + 1;
  }
  return count;
}

function isSpaceCode(code: number): boolean {
  return code === 32 || code === 9;
}
